import React from "react";
import CourseSectionSchedulerCourseSectionService from "../../../Services/CourseSectionSchedulerCourseSection.service";
import CourseSectionSchedulerBreakService from "../../../Services/CourseSectionSchedulerBreak.service";
import CourseSectionService from "../../../Services/CourseSection.service";
import useTempList from "../../Hooks/useTempList";
const colors = [
    "#007bff",
    "#fd7e14",
    "#6610f2",
    "#20c997",
    "#ffc107",
    "#e83e8c",
];
/**
 * Maintains all the 'global' state for the course section scheduler
 */
const HomeController = ({ courses, courseActions, courseSections, courseSectionActions, courseGroups, courseGroupMap, term, breaks, breakActions, selectedCourseSectionIds, selectedBreakIds, selectedLocations, children, }) => {
    const getColorOfCourseGroup = React.useCallback((groupId) => {
        if (!courseGroups.data) {
            return null;
        }
        const index = (courses.data || []).length +
            courseGroups.data.findIndex((g) => groupId === g.id);
        return colors[index % colors.length];
    }, [courseGroups.data, courses.data]);
    const getColorOfCourse = React.useCallback((courseId) => {
        if (!courses.data) {
            return null;
        }
        const definedCourses = courses.data; // avoid type refinement invalidation
        // If the course is in a group, use that group's color.
        // If the course is on it's own, use the index for that
        // course.
        // First we need to find the course's group.
        const groupId = Object.keys(courseGroupMap).find((key) => 
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'.
        courseGroupMap[key].includes(courseId));
        if (!groupId) {
            // We need a list of all courses that don't have a course group.
            const coursesNotInAGroup = definedCourses.filter((course) => !Object.keys(courseGroupMap).find((key) => 
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            courseGroupMap[key].includes(course.id)));
            const courseSectionIndex = coursesNotInAGroup.findIndex(
            // @ts-expect-error - TS2367 - This condition will always return 'false' since the types 'number' and 'string' have no overlap.
            (course) => course.id === courseId);
            if (courseSectionIndex === -1) {
                return null;
            }
            return colors[courseSectionIndex % colors.length];
        }
        // Return the color of the courseGroup
        return getColorOfCourseGroup(groupId);
    }, [courseGroupMap, courses.data, getColorOfCourseGroup]);
    // Course Sections
    const [allSelectedCourseSectionIds, tempCourseSectionIds, courseSectionIdActions,] = useTempList(selectedCourseSectionIds.data || []);
    const setSelectedCourseSections = (secs) => {
        // Figure out additions, i.e. which secs are in secs but not in our state.
        const additions = secs.filter((id) => !(allSelectedCourseSectionIds || []).includes(id));
        const removals = (allSelectedCourseSectionIds || []).filter((id) => !secs.includes(id));
        additions.forEach((id) => {
            const termId = term.data && term.data.id;
            if (!termId) {
                return;
            }
            courseSectionIdActions.add(id, () => CourseSectionSchedulerCourseSectionService.selectCourseSection(termId, id));
        });
        removals.forEach((id) => {
            const termId = term.data && term.data.id;
            if (!termId) {
                return;
            }
            courseSectionIdActions.remove(id, () => CourseSectionSchedulerCourseSectionService.unselectCourseSection(termId, id));
        });
    };
    const selectedCourseSections = React.useMemo(() => {
        return (courseSections.data || []).filter((sec) => (allSelectedCourseSectionIds || []).includes(sec.id));
    }, [allSelectedCourseSectionIds, courseSections.data]);
    const tempCourseSections = React.useMemo(() => (courseSections.data || []).filter((sec) => tempCourseSectionIds.includes(sec.id)), [courseSections.data, tempCourseSectionIds]);
    const [allSelectedBreakIds, tempSelectedBreakIds, selectedBreakIdActions] = useTempList(selectedBreakIds.data || []);
    const setSelectedBreaks = (brks) => {
        // Figure out additions, i.e. which secs are in secs but not in our state.
        const additions = brks.filter((id) => !allSelectedBreakIds.includes(id));
        const removals = allSelectedBreakIds.filter((id) => !brks.includes(id));
        additions.forEach((id) => {
            const termId = term.data && term.data.id;
            if (!termId) {
                return;
            }
            selectedBreakIdActions.add(id, () => CourseSectionSchedulerBreakService.selectBreak(termId, id));
        });
        removals.forEach((id) => {
            const termId = term.data && term.data.id;
            if (!termId) {
                return;
            }
            selectedBreakIdActions.remove(id, () => CourseSectionSchedulerBreakService.unselectBreak(termId, id));
        });
    };
    const selectedBreaks = React.useMemo(() => {
        return (breaks.data || []).filter((brk) => allSelectedBreakIds.includes(brk.id));
    }, [allSelectedBreakIds, breaks.data]);
    const tempBreaks = React.useMemo(() => (breaks.data || []).filter((brk) => tempSelectedBreakIds.includes(brk.id)), [breaks.data, tempSelectedBreakIds]);
    // Locations
    const [additionalSelectedLocations, setSelectedLocations] = React.useState(null);
    // Courses
    const [loadedCourseIds, loadingCourseIds, loadCourseActions] = useTempList([]);
    const loadCourse = (courseId) => {
        const termId = term.data && term.data.id;
        if (!termId) {
            return;
        }
        // @ts-expect-error - TS2345 - Argument of type 'number' is not assignable to parameter of type 'never'.
        loadCourseActions.add(courseId, () => CourseSectionService.getSchedulerCourseSections(
        // @ts-expect-error - TS2345 - Argument of type 'number' is not assignable to parameter of type 'string'.
        courseId, termId).then((cs) => cs.sections.forEach((section) => courseSectionActions.update(section))));
    };
    return children
        ? children({
            courses,
            loadedCourseIds,
            loadingCourseIds,
            loadCourse,
            courseSections,
            courseGroups,
            courseGroupMap,
            breaks,
            selectedLocations: additionalSelectedLocations || selectedLocations.data || [],
            selectedCourseSections,
            selectedBreaks,
            term,
            setSelectedCourseSections,
            setSelectedBreaks,
            // @ts-expect-error - TS2322 - Type '(courseId: string) => string | null | undefined' is not assignable to type '(arg1: number) => string | null | undefined'.
            getColorOfCourse,
            getColorOfCourseGroup,
            tempCourseSections,
            tempBreaks,
            addBreak: breakActions.update,
            editBreak: breakActions.update,
            deleteBreak: breakActions.deleteItem,
            setSelectedLocations,
        })
        : null;
};
export default HomeController;
